<script setup lang="ts">
import type { CustomFieldDef } from '~/types/blog'
import type { DropdownValue } from '~/types/ui'
import { readFieldValue } from '~/composables/useCustomFields'

const props = defineProps<{
  fields: Record<string, unknown>
  def: CustomFieldDef
}>()

const emit = defineEmits<{ (e: 'update', payload: { key: string; value: unknown }): void }>()

const options = computed(() => {
  if (!props.def.options) {
    return []
  }
  try {
    const parsed = JSON.parse(props.def.options)
    if (Array.isArray(parsed)) {
      return parsed.map(item => {
        if (typeof item === 'object') {
          const data = item as { label?: string; value?: string }
          return { label: data.label || data.value || '', value: data.value || data.label || '' }
        }
        return { label: String(item), value: String(item) }
      })
    }
  } catch {
    // fall back to split mode
  }
  return String(props.def.options)
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => ({ label: item, value: item }))
})

const currentValue = computed(() => readFieldValue(props.fields, props.def.key, props.def.default || ''))

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement
  emit('update', { key: props.def.key, value: target.value })
}

const onCheckbox = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update', { key: props.def.key, value: target.checked })
}

// onSelect 将单选或多选结果写回自定义字段
const onSelect = (value: DropdownValue | DropdownValue[]) => emit('update', { key: props.def.key, value })
</script>

<template>
  <label class="field-input">
    <span>
      {{ def.label || def.key }}
      <em v-if="def.required">*</em>
    </span>

    <BlogResizableTextarea v-if="def.type === 'json'">
      <textarea :value="String(currentValue ?? '')" rows="3" @input="onInput" />
    </BlogResizableTextarea>

    <input
      v-else-if="def.type === 'number'"
      :value="String(currentValue ?? '')"
      type="number"
      @input="onInput"
    />

    <input
      v-else-if="def.type === 'boolean'"
      :checked="Boolean(currentValue)"
      type="checkbox"
      @change="onCheckbox"
    />

    <BlogDropdown
      v-else-if="def.type === 'select' || def.type === 'radio'"
      :model-value="String(currentValue ?? '')"
      :options="[{ value: '', label: '请选择' }, ...options]"
      :label="def.label || def.key"
      variant="input"
      align="start"
      @update:model-value="onSelect"
    />

    <BlogDropdown
      v-else-if="def.type === 'checkbox'"
      :model-value="Array.isArray(currentValue) ? currentValue.map(String) : []"
      :options="options"
      :label="def.label || def.key"
      variant="input"
      align="start"
      multiple
      @update:model-value="onSelect"
    />

    <input v-else :value="String(currentValue ?? '')" type="text" @input="onInput" />

    <small v-if="def.remark">{{ def.remark }}</small>
  </label>
</template>

<style scoped>
.field-input {
  display: grid;
  gap: 6px;
}

.field-input span {
  font-size: 13px;
  color: var(--text-soft);
}

.field-input em {
  color: #d64545;
}

input,
textarea {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  background: var(--editor-bg);
}

small {
  color: var(--text-soft);
}
</style>
